import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getContactsByCustomerId,
  getCustomerById,
  getEmails,
} from "../../api/Customer/customerApi";
import {
  buildClientOfferHtmlDraft,
  buildClientOfferSubject,
  getUniboxAccounts,
  resolvePresupuestoTemporada,
  sendEstimateClientOffer,
} from "../../api/Estimates/estimateRfqApi";
import { buildBudgetPdfFile } from "../../utils/printBudgetPdf";

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_MB = 15;

function resolveModelo(presupuesto) {
  return (
    String(
      presupuesto?.DiseñoMuestra ||
        presupuesto?.NombrePresupuesto ||
        presupuesto?.designCliente ||
        "",
    ).trim() || "—"
  );
}

/** Evita meter el IdCliente numérico en el asunto como si fuera el nombre. */
function resolveClientLabel(raw) {
  const name = String(raw || "").trim();
  if (!name || /^\d+$/.test(name)) return "";
  return name;
}

function parseEmailTokens(raw) {
  return String(raw || "")
    .split(/[,;\s]+/)
    .map((s) => s.trim().toLowerCase())
    .filter((e) => e && e.includes("@"));
}

function mergeEmailList(values) {
  const seen = new Set();
  const out = [];
  for (const v of values || []) {
    const raw = typeof v === "string" ? v : v?.email;
    for (const e of parseEmailTokens(raw)) {
      if (seen.has(e)) continue;
      seen.add(e);
      out.push(e);
    }
  }
  return out;
}

/**
 * Envía oferta / presupuesto al cliente vía Unibox.
 * Si markOffered=true, el BACK marca Offered solo tras el envío OK.
 */
export default function EstimateEnviarOfertaClienteDialog({
  open,
  onClose,
  presupuesto,
  lines = [],
  markOffered = true,
  onSent,
  precioUnitario,
  totalVenta,
  moneda,
}) {
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [contactEmails, setContactEmails] = useState([]);
  const [clienteNombre, setClienteNombre] = useState("");
  const [toEmails, setToEmails] = useState([]);
  const [ccEmail, setCcEmail] = useState("");
  const [lang, setLang] = useState("en");
  const [notas, setNotas] = useState("");
  const [subject, setSubject] = useState("");
  const [files, setFiles] = useState([]);
  const [attachPdf, setAttachPdf] = useState(true);
  const [buildingPdf, setBuildingPdf] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const idCliente = presupuesto?.IdCliente;
  const isEs = lang === "es";
  const lineCount = useMemo(
    () => (Array.isArray(lines) ? lines.length : 0),
    [lines],
  );
  const hasLines = lineCount > 0;
  const modelo = useMemo(() => resolveModelo(presupuesto), [presupuesto]);
  const resolvedMoneda = useMemo(() => {
    const fromProp = String(moneda || "").trim();
    if (fromProp) return fromProp;
    const fromLine = (Array.isArray(lines) ? lines : []).find(
      (l) => l?.MonedaVenta,
    )?.MonedaVenta;
    return String(fromLine || "EUR").trim() || "EUR";
  }, [moneda, lines]);

  const previewHtml = useMemo(
    () =>
      buildClientOfferHtmlDraft({
        lang,
        numeroPresupuesto: presupuesto?.NumeroPresupuesto,
        nombrePresupuesto: presupuesto?.NombrePresupuesto,
        units: presupuesto?.Units,
        temporada: resolvePresupuestoTemporada(presupuesto),
        diseno: presupuesto?.DiseñoMuestra || presupuesto?.designCliente || "",
        notas,
        precioUnitario,
        totalVenta,
        moneda: resolvedMoneda,
      }),
    [lang, presupuesto, notas, precioUnitario, totalVenta, resolvedMoneda],
  );

  const emailOptions = useMemo(() => {
    const selected = new Set(toEmails.map((e) => e.toLowerCase()));
    const seen = new Set();
    const out = [];
    for (const e of contactEmails) {
      const email = String(e.email || "").trim();
      if (!email) continue;
      const key = email.toLowerCase();
      if (seen.has(key) || selected.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
    return out;
  }, [contactEmails, toEmails]);

  const buildSubject = (clientName, langCode) =>
    buildClientOfferSubject({
      cliente:
        resolveClientLabel(clientName) ||
        resolveClientLabel(presupuesto?.ClienteNombre) ||
        resolveClientLabel(presupuesto?.MarcaNombre) ||
        "",
      modelo,
      numeroPresupuesto: presupuesto?.NumeroPresupuesto,
      lang: langCode,
    });

  useEffect(() => {
    if (!open) return;
    setError("");
    setSending(false);
    setBuildingPdf(false);
    setCcEmail("");
    setNotas("");
    setFiles([]);
    setAttachPdf(true);
    setLang("en");

    let cancelled = false;
    (async () => {
      setLoadingAccounts(true);
      try {
        const list = await getUniboxAccounts();
        if (cancelled) return;
        setAccounts(list);
        setFromEmail(list.find((a) => a?.email)?.email || "");
      } catch (err) {
        if (!cancelled) {
          setAccounts([]);
          setFromEmail("");
          setError(
            err?.response?.data?.error ||
              "No se pudieron cargar las cuentas TONBOX. ¿Tienes buzón conectado?",
          );
        }
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, presupuesto?.IdPresupuesto]);

  useEffect(() => {
    if (!open || !idCliente) {
      const safe = resolveClientLabel(presupuesto?.ClienteNombre);
      setContactEmails([]);
      setClienteNombre(safe);
      setToEmails([]);
      setSubject(buildSubject(safe, lang));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [customer, contacts, emails] = await Promise.all([
          getCustomerById(idCliente).catch(() => null),
          getContactsByCustomerId(idCliente).catch(() => []),
          getEmails(idCliente).catch(() => []),
        ]);
        if (cancelled) return;
        const name = resolveClientLabel(
          customer?.name ||
            customer?.NombreCliente ||
            customer?.nombre ||
            customer?.Name ||
            presupuesto?.ClienteNombre ||
            presupuesto?.MarcaNombre ||
            "",
        );
        setClienteNombre(name);
        setSubject(buildSubject(name, lang));
        const list = [];
        for (const c of Array.isArray(contacts) ? contacts : []) {
          const email = String(c.Email || c.email || "").trim();
          if (!email) continue;
          list.push({
            email,
            label: c.Nombre || c.nombre || c.Name || email,
            source: "contact",
          });
        }
        for (const e of Array.isArray(emails) ? emails : []) {
          const email = String(e.Email || e.email || e.direccion || "").trim();
          if (!email) continue;
          list.push({
            email,
            label: e.Tipo || e.tipo || email,
            source: "email",
          });
        }
        const customerEmail = String(
          customer?.Email || customer?.email || "",
        ).trim();
        if (customerEmail) {
          list.unshift({
            email: customerEmail,
            label: isEs ? "Ficha cliente" : "Client card",
            source: "customer",
          });
        }
        setContactEmails(list);
        setToEmails(
          list[0]?.email ? [String(list[0].email).trim().toLowerCase()] : [],
        );
      } catch {
        if (!cancelled) {
          setContactEmails([]);
          setToEmails([]);
          const safe = resolveClientLabel(presupuesto?.ClienteNombre);
          setClienteNombre(safe);
          setSubject(buildSubject(safe, lang));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idCliente]);

  useEffect(() => {
    if (!open) return;
    setSubject((prev) => {
      const autoEs = buildSubject(clienteNombre, "es");
      const autoEn = buildSubject(clienteNombre, "en");
      if (!prev || prev === autoEs || prev === autoEn) {
        return buildSubject(clienteNombre, lang);
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, open, clienteNombre, modelo, presupuesto?.NumeroPresupuesto]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setError("");
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_ATTACHMENTS) {
        setError(`Máximo ${MAX_ATTACHMENTS} adjuntos.`);
        break;
      }
      if (file.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
        setError(`"${file.name}" supera ${MAX_ATTACHMENT_MB} MB.`);
        continue;
      }
      const dup = next.some(
        (f) => f.name === file.name && f.size === file.size,
      );
      if (!dup) next.push(file);
    }
    setFiles(next);
  };

  const handleSend = async () => {
    setError("");
    if (!presupuesto?.IdPresupuesto) {
      setError("Guarda el presupuesto antes de enviar la oferta.");
      return;
    }
    if (!hasLines) {
      setError(
        isEs
          ? "El presupuesto no tiene líneas. Añade costes al escandallo antes de enviar la oferta."
          : "This estimate has no lines. Add cost sheet lines before sending the offer.",
      );
      return;
    }
    if (!fromEmail) {
      setError("Selecciona la cuenta de envío (TONBOX).");
      return;
    }
    if (!toEmails.length) {
      setError(
        isEs
          ? "Indica al menos un email del cliente."
          : "Enter at least one client email.",
      );
      return;
    }

    setSending(true);
    try {
      const html = buildClientOfferHtmlDraft({
        lang,
        numeroPresupuesto: presupuesto.NumeroPresupuesto,
        nombrePresupuesto: presupuesto.NombrePresupuesto,
        units: presupuesto.Units,
        temporada: resolvePresupuestoTemporada(presupuesto),
        diseno: presupuesto.DiseñoMuestra || presupuesto.designCliente || "",
        notas,
        precioUnitario,
        totalVenta,
        moneda: resolvedMoneda,
      });

      const attachList = [...files];
      if (attachPdf) {
        setBuildingPdf(true);
        try {
          const pdfFile = await buildBudgetPdfFile({
            idPresupuesto: presupuesto.IdPresupuesto,
            nombreProducto:
              presupuesto.DiseñoMuestra ||
              presupuesto.designCliente ||
              presupuesto.NombrePresupuesto,
            unidades: presupuesto.Units,
            proveedorNombre: presupuesto.ProveedorNombre,
            precioUnitario,
            totalVenta,
            moneda:
              moneda ||
              (Array.isArray(lines) &&
                lines.find((l) => l?.MonedaVenta)?.MonedaVenta) ||
              undefined,
            // En Created aún no hay FechaPresentacion (se setea al marcar Offered).
            // Usar hoy para que Quotation Date no salga vacío en el PDF adjunto.
            fechaPresupuesto:
              presupuesto.FechaPresentacion ||
              presupuesto.FechaAceptacion ||
              new Date(),
          });
          if (pdfFile) attachList.unshift(pdfFile);
        } catch (pdfErr) {
          throw new Error(
            pdfErr?.message ||
              "No se pudo generar el PDF del presupuesto para adjuntar.",
          );
        } finally {
          setBuildingPdf(false);
        }
      }

      const result = await sendEstimateClientOffer(
        presupuesto.IdPresupuesto,
        {
          fromEmail,
          toEmail: toEmails.join(", "),
          ccEmail: String(ccEmail || "").trim(),
          subject,
          html,
          lang,
          clienteNombre,
          modelo,
          markOffered: markOffered ? "true" : "false",
        },
        attachList,
      );

      if (typeof onSent === "function") {
        onSent(result);
      }
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          "No se pudo enviar la oferta al cliente",
      );
    } finally {
      setBuildingPdf(false);
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 800 }}>
        {markOffered
          ? isEs
            ? "Enviar oferta al cliente (Offered)"
            : "Send offer to client (Offered)"
          : isEs
            ? "Reenviar oferta al cliente"
            : "Resend offer to client"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.75} sx={{ pt: 0.5 }}>
          {markOffered ? (
            <Alert severity="info">
              {isEs
                ? "El presupuesto solo pasará a Offered si el email se envía correctamente. Si cancelas, no cambia el estado."
                : "The estimate becomes Offered only after the email is sent successfully. Cancel keeps the current status."}
            </Alert>
          ) : null}

          {!hasLines ? (
            <Alert severity="warning">
              {isEs
                ? "Este presupuesto no tiene líneas en el escandallo. No se puede enviar la oferta."
                : "This estimate has no cost-sheet lines. The offer cannot be sent."}
            </Alert>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Chip
              size="small"
              label={presupuesto?.NumeroPresupuesto || "—"}
              sx={{ fontWeight: 700 }}
            />
            {clienteNombre ? (
              <Chip size="small" variant="outlined" label={clienteNombre} />
            ) : null}
            <Chip size="small" variant="outlined" label={modelo} />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={lang}
              onChange={(_e, v) => v && setLang(v)}
            >
              <ToggleButton value="en">EN</ToggleButton>
              <ToggleButton value="es">ES</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <FormControl fullWidth size="small" disabled={loadingAccounts}>
            <InputLabel id="client-offer-from">From (TONBOX)</InputLabel>
            <Select
              labelId="client-offer-from"
              label="From (TONBOX)"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
            >
              {accounts.map((a) => (
                <MenuItem key={a.email || a.id} value={a.email}>
                  {a.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={emailOptions}
            value={toEmails}
            onChange={(_e, value) => setToEmails(mergeEmailList(value))}
            getOptionLabel={(o) => {
              if (typeof o === "string") return o;
              if (!o?.email) return "";
              return o.label && o.label !== o.email
                ? `${o.label} · ${o.email}`
                : o.email;
            }}
            isOptionEqualToValue={(a, b) => {
              const ea =
                typeof a === "string"
                  ? a.toLowerCase()
                  : String(a?.email || "").toLowerCase();
              const eb =
                typeof b === "string"
                  ? b.toLowerCase()
                  : String(b?.email || "").toLowerCase();
              return ea === eb;
            }}
            renderTags={(value, getTagProps) =>
              value.map((email, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip key={key} size="small" label={email} {...tagProps} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={isEs ? "Para (emails)" : "To (emails)"}
                size="small"
                helperText={
                  !idCliente
                    ? isEs
                      ? "El presupuesto no tiene cliente asignado"
                      : "Estimate has no client assigned"
                    : isEs
                      ? "Elige contactos o escribe emails (Enter / coma para varios)"
                      : "Pick contacts or type emails (Enter / comma for several)"
                }
              />
            )}
          />

          <TextField
            label="CC"
            size="small"
            fullWidth
            value={ccEmail}
            onChange={(e) => setCcEmail(e.target.value)}
          />

          <TextField
            label={isEs ? "Asunto" : "Subject"}
            size="small"
            fullWidth
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />

          <TextField
            label={isEs ? "Notas (en el cuerpo)" : "Notes (in body)"}
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />

          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                mb: 0.5,
                display: "block",
              }}
            >
              {isEs ? "Vista previa del cuerpo" : "Body preview"}
            </Typography>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                p: 1.5,
                bgcolor: "#fafafa",
                maxHeight: 280,
                overflow: "auto",
                "& table": {
                  width: "100% !important",
                  maxWidth: "100% !important",
                },
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
          >
            <Button
              size="small"
              variant={attachPdf ? "contained" : "outlined"}
              onClick={() => setAttachPdf((v) => !v)}
              sx={{
                bgcolor: attachPdf ? "#00c2a3" : undefined,
                "&:hover": { bgcolor: attachPdf ? "#00a88c" : undefined },
                fontWeight: 700,
              }}
            >
              {attachPdf
                ? isEs
                  ? "PDF presupuesto: sí"
                  : "Budget PDF: on"
                : isEs
                  ? "PDF presupuesto: no"
                  : "Budget PDF: off"}
            </Button>
            <Button
              component="label"
              size="small"
              startIcon={<AttachFileIcon />}
              variant="outlined"
            >
              {isEs ? "Más adjuntos" : "More files"}
              <input
                hidden
                type="file"
                multiple
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
              />
            </Button>
          </Stack>

          {files.length ? (
            <Box>
              {files.map((f, idx) => (
                <Stack
                  key={`${f.name}-${idx}`}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ py: 0.25 }}
                >
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {f.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, i) => i !== idx))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending}>
          {isEs ? "Cancelar" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || loadingAccounts || !hasLines}
          startIcon={
            sending || buildingPdf ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
          sx={{
            bgcolor: "#00c2a3",
            fontWeight: 800,
            "&:hover": { bgcolor: "#00a88c" },
          }}
        >
          {buildingPdf
            ? isEs
              ? "Generando PDF…"
              : "Building PDF…"
            : sending
              ? isEs
                ? "Enviando…"
                : "Sending…"
              : isEs
                ? "Enviar oferta"
                : "Send offer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
